import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calculator, Link, Pencil, X } from 'lucide-react';
import { useSelector } from 'react-redux';
import { Toast } from '../../helpers/alert';
import { confirmDiscardChanges } from '../plan3d/usePendingChanges';
import { canManageProjects } from '../../pages/Projects/projects.helpers';
import { createCalculationModelsApi } from './api';
import { CalculationModelWorkspace } from './ModelEditorPage';
import GuidedImpactCalculation from './GuidedImpactCalculation';
import { toDisplayText } from './model';
import {
  activePrimaryBinding,
  draftOf,
  findProjectModel,
  latestPublishedOf,
  outputNamesFromGraph,
  projectModelCode,
  versionIdOf,
} from './projectCalculationModel';
import './projectCalculationModel.css';

export default function ProjectCalculationModelSection({ project, initialMode = null, onWorkspaceChange, onConfigurationChange }) {
  const currentUser = useSelector(state => state.user.currentUser);
  const token = currentUser?.accessToken;
  const authenticated = Boolean(token);
  const canWrite = canManageProjects(currentUser?.privilege);
  const api = useMemo(() => createCalculationModelsApi(token), [token]);
  const apiRef = useRef(api);
  const creationPromiseRef = useRef(null);
  const [mode, setMode] = useState(initialMode);
  const [model, setModel] = useState(null);
  const [versionId, setVersionId] = useState(null);
  const [binding, setBinding] = useState(null);
  const [unboundPublished, setUnboundPublished] = useState(null);
  const [outputs, setOutputs] = useState([]);
  const [summaryGraph, setSummaryGraph] = useState(null);
  const [graph, setGraph] = useState(null);
  const [editorView, setEditorView] = useState('guided');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bindingBusy, setBindingBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editorDirty, setEditorDirty] = useState(false);
  const sourceScope = useMemo(() => ({
    companyId: project?.company_id == null ? null : Number(project.company_id),
    branchId: project?.branch_id == null ? null : Number(project.branch_id),
    branchName: toDisplayText(project?.branch?.office_name ?? project?.branch?.name ?? project?.branch_name, ''),
  }), [project?.branch?.name, project?.branch?.office_name, project?.branch_id, project?.branch_name, project?.company_id]);

  useEffect(() => { apiRef.current = api; }, [api]);
  useEffect(() => { onWorkspaceChange?.(Boolean(mode)); }, [mode, onWorkspaceChange]);
  useEffect(() => setMode(initialMode), [initialMode]);

  const loadState = useCallback(async () => {
    if (!authenticated || !project?.id) return;
    setLoading(true); setError('');
    try {
      const currentApi = apiRef.current;
      const bindings = await currentApi.projectBindings(project.id);
      const primary = activePrimaryBinding(bindings);
      let modelData = primary?.model || null;
      if (modelData?.id) modelData = await currentApi.model(modelData.id);
      if (!modelData) {
        const models = await currentApi.listModels(project.company_id);
        const candidate = findProjectModel(models, project.id);
        if (candidate?.id) modelData = await currentApi.model(candidate.id);
      }
      if (!modelData && mode === 'edit' && canWrite) {
        if (!creationPromiseRef.current) {
          creationPromiseRef.current = currentApi.createModel({
            name: `Cálculo - ${project.title}`,
            description: `Modelo de cálculo del proyecto ${project.title}`,
            code: projectModelCode(project.id),
            company_id: Number(project.company_id),
            default_timezone: 'America/Argentina/Buenos_Aires',
          });
        }
        const created = await creationPromiseRef.current;
        modelData = { ...created.model, versions: [created.draft] };
      }
      if (!modelData) {
        setModel(null); setVersionId(null); setBinding(null); setUnboundPublished(null); setOutputs([]); setSummaryGraph(null);
        return;
      }

      const activeDraft = draftOf(modelData);
      const latestPublished = latestPublishedOf(modelData);
      const boundVersionId = primary?.version_id ?? versionIdOf(primary?.version);
      const published = (modelData.versions || []).find(item => Number(versionIdOf(item)) === Number(boundVersionId)) || latestPublished;
      const summaryVersion = published || activeDraft;
      if (summaryVersion) {
        const loadedSummaryGraph = await currentApi.version(modelData.id, versionIdOf(summaryVersion));
        setOutputs(outputNamesFromGraph(loadedSummaryGraph));
        setSummaryGraph(loadedSummaryGraph);
      } else { setOutputs([]); setSummaryGraph(null); }

      let targetVersion = null;
      if (!primary && latestPublished) {
        targetVersion = latestPublished;
        setUnboundPublished(latestPublished);
      } else {
        setUnboundPublished(null);
        if (mode === 'edit' && canWrite) {
          if (activeDraft) targetVersion = activeDraft;
          else if (latestPublished) {
            try {
              targetVersion = await currentApi.createDraft(modelData.id);
            } catch (draftError) {
              if (draftError.status !== 409) throw draftError;
              const refreshed = await currentApi.model(modelData.id);
              modelData = refreshed;
              targetVersion = draftOf(refreshed);
            }
          }
        } else if (mode === 'view') targetVersion = published;
      }

      setModel(modelData);
      setBinding(primary || null);
      setVersionId(versionIdOf(targetVersion));
      if (targetVersion) setGraph(await currentApi.version(modelData.id, versionIdOf(targetVersion)));
      else setGraph(null);
    } catch (loadError) {
      setError(loadError.message || 'No se pudo cargar el modelo de cálculo.');
    } finally {
      creationPromiseRef.current = null;
      setLoading(false);
    }
  }, [authenticated, canWrite, mode, project?.company_id, project?.id, project?.title, refreshKey]);

  useEffect(() => { loadState(); }, [loadState]);

  const openMode = nextMode => {
    setError('');
    setEditorView('guided');
    setMode(nextMode);
  };
  const openAdvanced = () => {
    setError('');
    setEditorView('advanced');
    setMode(canWrite ? 'edit' : 'view');
  };
  const closeWorkspace = async () => {
    if (editorDirty) {
      const confirmed = await confirmDiscardChanges({ confirmButtonText: 'Cerrar y descartar' });
      if (!confirmed) return;
    }
    setEditorDirty(false);
    setMode(null);
    setRefreshKey(value => value + 1);
  };
  const returnToGuided = async () => {
    if (editorDirty) {
      const confirmed = await confirmDiscardChanges({ confirmButtonText: 'Volver y descartar' });
      if (!confirmed) return;
    }
    const refreshed = await api.version(model.id, versionId);
    setGraph(refreshed);
    setEditorDirty(false);
    setEditorView('guided');
  };

  const associatePublishedVersion = useCallback(async published => {
    const linked = await api.bindProject({
      project_id: Number(project.id),
      model_id: Number(published.model_id || model?.id),
      version_id: Number(versionIdOf(published)),
      purpose: 'primary',
      is_active: true,
    });
    setBinding(linked);
    setUnboundPublished(null);
    setOutputs(outputNamesFromGraph(published));
    onConfigurationChange?.();
    return linked;
  }, [api, model?.id, onConfigurationChange, project.id]);

  const retryAssociation = async () => {
    if (!unboundPublished || bindingBusy) return;
    setBindingBusy(true);
    try {
      await associatePublishedVersion(unboundPublished);
      Toast.fire({ icon: 'success', title: 'Cálculo asociado al proyecto' });
    } catch (associationError) {
      Toast.fire({ icon: 'error', title: associationError.message || 'No se pudo asociar el cálculo al proyecto.' });
    } finally { setBindingBusy(false); }
  };

  const saveGuidedGraph = async (payload, action = 'save') => {
    const saved = await api.saveDraft(model.id, versionId, payload);
    setGraph(saved);
    setOutputs(outputNamesFromGraph(saved));
    if (action === 'save') {
      Toast.fire({ icon: 'success', title: 'Cálculo guardado' });
      return saved;
    }
    const validation = await api.validate(model.id, versionId);
    if (!validation.valid) {
      const first = validation.errors?.[0]?.message;
      throw new Error(first || 'El cálculo todavía tiene datos incompletos. Revisalo antes de publicar.');
    }
    if (action === 'review') {
      Toast.fire({ icon: 'success', title: 'El cálculo está listo para publicar' });
      return saved;
    }
    const published = await api.publish(model.id, versionId);
    setSummaryGraph(published);
    setGraph(published);
    setOutputs(outputNamesFromGraph(published));
    try {
      await associatePublishedVersion(published);
    } catch (associationError) {
      setUnboundPublished(published);
      throw new Error(associationError.message || 'El cálculo fue publicado, pero no pudo asociarse al proyecto.');
    }
    setMode(null);
    setRefreshKey(value => value + 1);
    Toast.fire({ icon: 'success', title: 'Cálculo publicado y asociado' });
    return published;
  };
  const summaryComponents = (summaryGraph?.nodes || [])
    .filter(node => ['source.basic_parameter', 'variable.manual', 'constant.number'].includes(node.type))
    .map(node => ({
      key: node.node_key,
      name: toDisplayText(node.name, node.type === 'variable.manual' ? 'Variable manual' : node.type === 'constant.number' ? 'Valor fijo' : 'Medición'),
      detail: node.type === 'constant.number' ? `${node.config_json?.value} ${node.config_json?.unit || ''}` : node.config_json?.unit || '',
    }));

  if (mode && (loading || error || !model || !versionId || !graph)) return <section className="project-calculation-workspace is-simple" aria-label="Cálculo del impacto">
    <div className="project-calculation-context-bar">
      <div className="project-calculation-breadcrumb"><span>Seguimiento</span><b>›</b><span>Cálculo del impacto</span><b>›</b><strong>Edición sencilla</strong></div>
      <button type="button" className="projects-btn projects-btn-secondary" onClick={closeWorkspace}><X size={16} />Cancelar edición</button>
    </div>
    <div className="pcm-state"><p>{loading ? 'Preparando el espacio de cálculo...' : error || 'No hay un cálculo publicado disponible para consultar.'}</p></div>
  </section>;

  if (mode && model && versionId && graph && !loading) return <section className={`project-calculation-workspace ${editorView === 'advanced' ? 'is-advanced' : 'is-simple'}`} aria-label="Cálculo del impacto">
    <div className="project-calculation-context-bar">
      <div className="project-calculation-breadcrumb"><span>Seguimiento</span><b>›</b><span>Cálculo del impacto</span><b>›</b><strong>{editorView === 'advanced' ? 'Editor avanzado' : mode === 'edit' ? 'Edición sencilla' : 'Vista del cálculo'}</strong></div>
      {editorView !== 'advanced' && <button type="button" className="projects-btn projects-btn-secondary" onClick={closeWorkspace}><X size={16} />Cancelar edición</button>}
    </div>
    {unboundPublished && !binding && <div className="pcm-binding-warning"><div><strong>El cálculo está publicado, pero todavía no está asociado al proyecto.</strong><span>Podés reintentar la asociación sin volver a publicarlo.</span></div>{canWrite && <button type="button" disabled={bindingBusy} onClick={retryAssociation}><Link size={16} />{bindingBusy ? 'Asociando…' : 'Reintentar asociación'}</button>}</div>}
    {editorView === 'guided' ? <GuidedImpactCalculation
      graph={graph}
      api={api}
      editable={graph.status === 'draft' && canWrite}
      onSaved={saved => { setGraph(saved); setOutputs(outputNamesFromGraph(saved)); }}
      onAdvanced={() => setEditorView('advanced')}
      onRequestEdit={() => openMode('edit')}
      organizationScope={sourceScope}
      onSaveGraph={saveGuidedGraph}
      workflowActions
    /> : <CalculationModelWorkspace
      modelId={model.id}
      versionId={versionId}
      projectContext
      canWrite={canWrite}
      onEditRequested={() => openMode('edit')}
      onPublished={associatePublishedVersion}
      onDirtyChange={setEditorDirty}
      onBack={returnToGuided}
      backLabel="Volver a vista sencilla"
      sourceScope={sourceScope}
    />}
  </section>;

  return <section className="projects-card project-calculation-section is-summary">
    <div className="projects-card-header projects-detail-section-header">
      <div><h2>Cálculo del impacto</h2><p>Define cómo SIGER obtiene los resultados mostrados arriba.</p></div>
      {model && <span className={`project-calculation-status ${draftOf(model) ? 'is-editing' : 'is-configured'}`}>{draftOf(model) ? 'Cambios en edición' : 'Cálculo configurado'}</span>}
    </div>
    <div className="project-calculation-body">
      {loading ? <p className="projects-inline-empty">Cargando cálculo...</p> : error ? <p className="project-calculation-error">{error}</p> : !model ? <>
        <div><strong>Este proyecto todavía no tiene un modelo de cálculo configurado.</strong><p>Podés combinar mediciones, variables y operaciones para representar su impacto energético.</p></div>
        {canWrite && <button type="button" className="projects-btn projects-btn-primary" onClick={() => openMode('edit')}><Calculator size={16} />Configurar cálculo</button>}
      </> : <>
        <div className="project-calculation-summary">{summaryComponents.length > 0 && <div className="project-calculation-components">{summaryComponents.map((component, index) => <div key={component.key}>{index > 0 && <b>+</b>}<span>{component.name}</span>{component.detail && <small>{component.detail}</small>}</div>)}</div>}<span>Resultado</span>{outputs.length ? <ul>{outputs.map((output, index) => <li key={`${output}-${index}`}>{output}</li>)}</ul> : <p>Todavía no se definieron resultados visibles.</p>}</div>
        <div className="project-calculation-actions">
          {canWrite && <button type="button" className="projects-btn projects-btn-primary" onClick={() => openMode('edit')}><Pencil size={16} />Editar cálculo</button>}
          <button type="button" className="projects-btn projects-btn-secondary" onClick={openAdvanced}><Calculator size={16} />Editor avanzado</button>
        </div>
      </>}
    </div>
  </section>;
}
